import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, HeartHandshake, Check, ArrowRight, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { TrialSignupForm } from "@/components/TrialSignupForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const READINESS = ["Show up consistently.", "Practice instead of perform.", "Be honest, even when it's uncomfortable.", "Learn through experience.", "Encourage and be encouraged.", "Remain curious.", "Be challenged.", "Commit to six months of intentional growth."];

const PARTICIPATION = [
  ["socal-in-person", "Southern California In-Person"],
  ["virtual", "Virtual Cohort"],
  ["either", "Either"],
  ["interested-near-me", "I'm interested when a cohort launches near me"],
];

const INVOLVEMENT = [
  "I'd love to simply participate in a cohort.",
  "I'd like to volunteer at Reflection Nights or future events.",
  "I'd love to host an Outpoured gathering in my home.",
  "I'd be interested in helping facilitate future cohorts.",
  "I'd love to help bring Outpoured to my church.",
  "I'd love to help bring Outpoured to my city.",
  "I'd be interested in becoming an Outpoured facilitator one day.",
  "I'd love to serve however needed.",
  "I'm not sure yet, but I'm excited to learn more.",
];

const overline = "tracking-overline text-xs uppercase text-[#A9724E]";

const PROCESS = [
  { n: "01", title: "You Apply", body: "Tell us your story. Share where you are, what you're navigating, and why you're drawn to this season." },
  { n: "02", title: "We Review", body: "By November 15, 2026, you'll receive an email with one of two messages — \u201CLet's Talk\u201D (we'd love to know more, and we'll invite you to a Discovery Conversation) or \u201CNot Right Now\u201D (this may not be your season yet — we believe in right timing and would love to stay connected for future offerings)." },
  { n: "03", title: "Discovery Conversation", note: "if invited", body: "Between November 15–30, a real, personal video conversation — 45–60 minutes to hear your heart, answer your questions, and confirm together whether this is the right fit. No scripts. Just listening." },
  { n: "04", title: "You're In", note: "if it's the right fit", body: "Acceptance emails go out November 30, followed by a welcome call in early December — with cohort community access and pre-cohort materials to prepare together before the Winter 2027 launch." },
];

const TIMELINE = [
  { when: "Now – November 8, 2026", what: "Applications open. Submit anytime before they close." },
  { when: "By November 15, 2026", what: "Invitations to a video conversation are sent to applicants." },
  { when: "November 15 – 30, 2026", what: "Discovery Conversations — a real, personal video call to explore fit." },
  { when: "November 30, 2026", what: "Acceptance emails go out." },
  { when: "Early December 2026", what: "Welcome calls with accepted applicants." },
  { when: "Winter 2027", what: "Your six-month formation journey officially begins." },
];

const KNOW = [
  "Right timing matters. If now isn't your season, we'll stay connected for future cohorts.",
  "Scholarships and flexibility options are available — don't let finances be a barrier. Just ask us.",
  "If accepted, you'll have time to prepare and connect with your cohort before launch.",
];

const FAQ = [
  { q: "Who is this for?", a: "VESSELS is for women ready to encounter God in ordinary life. Vows & Vessels is for dating, engaged, or married couples building something intentional together. If you're willing to show up honestly, you belong here." },
  { q: "What's the time commitment?", a: "Six months. Weekly in-person gatherings woven with virtual community, hands-on practices, and partner connections. It's a rhythm, not a burden — designed to fit into the life you're already living." },
  { q: "Is it in person or virtual?", a: "Both. Cohorts gather in person weekly, with virtual integration and community in between so the experience carries into your everyday life." },
  { q: "What if I can't afford it?", a: "Don't let finances be the reason you don't apply. Scholarship and flexible-payment options are available — just ask us on your application or email dej@outpouredliving.com." },
  { q: "What's the refund or cancellation policy?", a: "If you need to step away before the cohort begins, reach out and we'll make it right. Once a cohort is underway, spots are reserved and non-refundable — but scholarship and flexible payment options are available if cost is the barrier. Just ask." },
  { q: "What happens after I apply?", a: "Every application is personally reviewed. By November 15, 2026 you'll receive an email with next steps — an invitation to a Discovery Conversation, or a heartfelt not-right-now. Conversations happen Nov 15–30, and acceptances go out November 30." },
];

const DEADLINE = new Date("2026-11-08T23:59:59-08:00");

const Countdown = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const diff = DEADLINE.getTime() - now;
  if (diff <= 0) {
    return (
      <div data-testid="apply-countdown" className="mx-auto mb-10 inline-flex items-center gap-3 rounded-full border border-[#D8CFC0] bg-[#EAE3D9]/50 px-6 py-3">
        <Clock className="w-4 h-4 text-[#A9724E]" strokeWidth={1.5} />
        <span className="text-sm font-body text-[#5E4B3C]">Winter 2027 applications have closed — join the list for the next cohort.</span>
      </div>
    );
  }
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const units = [["Days", days], ["Hrs", hours], ["Min", mins]];
  return (
    <div data-testid="apply-countdown" className="mx-auto mb-10 inline-flex items-center gap-5 rounded-full border border-[#D8CFC0] bg-[#EAE3D9]/50 px-6 md:px-7 py-3">
      <span className="flex items-center gap-2 tracking-overline text-[10px] uppercase text-[#8a7a6a]"><Clock className="w-3.5 h-3.5 text-[#A9724E]" strokeWidth={1.5} />Applications close in</span>
      <div className="flex items-center gap-4">
        {units.map(([label, val]) => (
          <div key={label} className="flex flex-col items-center leading-none">
            <span className="font-serif-display text-2xl text-[#2A2825]">{val}</span>
            <span className="tracking-overline text-[9px] uppercase text-[#8a7a6a] mt-1">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
const inputCls = "bg-transparent border-0 border-b border-[#D1C9BE] rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[#A9724E]";
const areaCls = "bg-transparent border border-[#D1C9BE] rounded-none focus-visible:ring-0 focus-visible:border-[#A9724E] text-[#2A2825]";

const Label = ({ children, hint }) => (
  <label className="block text-[#2A2825] font-body mb-2 text-[15px]">{children}{hint && <span className="block text-xs text-[#8a7a6a] mt-1 font-normal">{hint}</span>}</label>
);

const SectionTitle = ({ n, title }) => (
  <div className="mb-8 pb-4 border-b border-[#D1C9BE]">
    <p className={overline}>Section {n}</p>
    <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#2A2825] mt-1">{title}</h2>
  </div>
);

export default function ApplyPage() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ cohort: "", participation: "", involvement: [], willingness: "", prioritize: "", launch_outside: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [ref, setRef] = useState("");

  useEffect(() => {
    const c = params.get("cohort");
    if (c === "vessels" || c === "vows-and-vessels") setF((s) => ({ ...s, cohort: c }));
  }, [params]);

  useEffect(() => {
    if (window.location.hash === "#trial") {
      setTimeout(() => document.getElementById("trial")?.scrollIntoView({ behavior: "smooth", block: "start" }), 450);
    }
  }, []);

  // Load saved progress
  useEffect(() => {
    try {
      const saved = localStorage.getItem("outpoured-application");
      if (saved) setF((s) => ({ ...s, ...JSON.parse(saved) }));
    } catch (e) {}
  }, []);

  // Auto-save progress
  useEffect(() => {
    try { localStorage.setItem("outpoured-application", JSON.stringify(f)); } catch (e) {}
  }, [f]);

  // Capture a lightweight draft (email + cohort) so we can send a gentle deadline reminder
  useEffect(() => {
    if (done) return;
    const email = (f.email || "").trim();
    if (!f.cohort || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    const t = setTimeout(() => {
      axios.post(`${API}/application/draft`, {
        email,
        track: f.cohort,
        name: `${f.firstName || ""} ${f.lastName || ""}`.trim() || null,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [f.email, f.cohort, f.firstName, f.lastName, done]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleInvolve = (v) => setF((s) => ({ ...s, involvement: s.involvement.includes(v) ? s.involvement.filter((x) => x !== v) : [...s.involvement, v] }));

  const chooseCohort = (c) => { set("cohort", c); setTimeout(() => document.getElementById("application-form")?.scrollIntoView({ behavior: "smooth" }), 100); };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.cohort) { toast.error("Please choose a cohort above."); return; }
    if (!f.firstName || !f.email) { toast.error("Please share your name and email."); return; }
    setLoading(true);
    const { firstName, lastName, email, ...rest } = f;
    try {
      const res = await axios.post(`${API}/waitlist`, {
        name: `${firstName} ${lastName || ""}`.trim(),
        email,
        track: f.cohort,
        answers: { firstName, lastName, ...rest },
      });
      setRef(res.data?.ref || "");
      setDone(true);
      try { localStorage.removeItem("outpoured-application"); } catch (e) {}
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) return <ThankYou cohort={f.cohort} refNo={ref} />;

  return (
    <div className="bg-[#F4EFE6] text-[#2A2825]">
      {/* HERO */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-14 text-center">
        <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className={overline + " mb-5"}>Now Accepting Applications · Winter 2027</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-serif-display font-light text-5xl sm:text-6xl leading-[1.05] mb-5">Begin Your Journey</motion.h1>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.2 }} className="mb-8">
          <p className="font-serif-display text-xl md:text-2xl italic text-[#5E4B3C]">Vessels &amp; VV</p>
          <p className="tracking-overline text-xs uppercase text-[#A9724E] mt-3">Application Deadline</p>
          <p className="font-serif-display text-lg md:text-xl text-[#2A2825] mt-1">November 8, 2026</p>
        </motion.div>
        <Countdown />
        <p className="text-[#5E4B3C] leading-relaxed font-body text-lg max-w-2xl mx-auto mb-8">Joining an Outpoured cohort isn't about proving you're ready. It's about being willing to become. Every cohort is intentionally small, and every application is personally reviewed to help us create the best possible experience for every participant.</p>
        <div data-testid="application-intro-note" className="border border-[#D8CFC0] bg-[#EAE3D9]/50 px-6 py-5 max-w-2xl mx-auto text-left">
          <p className="text-sm text-[#5E4B3C] font-body leading-relaxed">
            This application takes approximately <strong>15 minutes</strong>. Your progress saves automatically — you can step away and return anytime.
          </p>
          <div className="text-sm text-[#2A2825] font-body leading-relaxed mt-3">
            <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-2">Investment</p>
            <div className="mb-3">
              <p className="font-serif-display text-base text-[#2A2825] mb-0.5">VESSELS</p>
              <p>$1,997 one-time</p>
              <p className="italic text-[#8a7a6a]">or</p>
              <p>$375/month for 6 months</p>
            </div>
            <div>
              <p className="font-serif-display text-base text-[#2A2825] mb-0.5">Vows &amp; Vessels</p>
              <p>$2,697 one-time</p>
              <p className="italic text-[#8a7a6a]">or</p>
              <p>$499/month for 6 months</p>
            </div>
          </div>
          <p className="text-xs text-[#8a7a6a] font-body mt-1">Scholarship options available upon request.</p>
        </div>
      </section>

      {/* CHOOSE COHORT */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-8">
        <p className={overline + " mb-6"}>Choose your cohort</p>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { id: "vessels", icon: Users, name: "VESSELS", sub: "A Six-Month Formation Journey for Women", body: "Through hands-on experiences, intentional community, partner connection, Scripture, and weekly challenges, VESSELS helps women recognize how God is working through the stories they're already living." },
            { id: "vows-and-vessels", icon: HeartHandshake, name: "Vows & Vessels", sub: "A Six-Month Journey for Couples", body: "Together, couples participate in immersive experiences, relationship challenges, guided conversations, and community that help them recognize God's work within their relationship and build a Christ-centered life together." },
          ].map((c) => (
            <button key={c.id} type="button" data-testid={`choose-${c.id}`} onClick={() => chooseCohort(c.id)} className={`text-left border p-8 transition-colors duration-500 ${f.cohort === c.id ? "border-[#A9724E] bg-[#EAE3D9]/50" : "border-[#D1C9BE] hover:border-[#5E4B3C]/50"}`}>
              <div className="flex items-center justify-between mb-4">
                <c.icon className="w-6 h-6 text-[#A9724E]" strokeWidth={1.5} />
                {f.cohort === c.id && <span className="text-xs tracking-overline uppercase text-[#A9724E] flex items-center gap-1"><Check className="w-4 h-4" /> Selected</span>}
              </div>
              <h3 className="font-serif-display text-3xl mb-2">{c.name}</h3>
              <p className="font-serif-display text-lg italic text-[#5E4B3C] mb-4">{c.sub}</p>
              <p className="text-sm text-[#5E4B3C] leading-relaxed font-body">{c.body}</p>
            </button>
          ))}
        </div>
      </section>

      {/* FREE 7-DAY TASTE */}
      <section id="trial" data-testid="apply-trial" className="max-w-4xl mx-auto px-6 md:px-12 py-14 scroll-mt-24">
        <div className="border border-[#A9724E]/30 bg-[#EAE3D9]/40 rounded-2xl p-7 md:p-10">
          <p className={overline + " mb-3"}>Not ready to apply?</p>
          <h2 className="font-serif-display font-light text-3xl md:text-4xl leading-tight mb-3">Try a free 7-day taste first.</h2>
          <p className="text-[#5E4B3C] font-body leading-relaxed max-w-2xl mb-6">
            Get a small taste of the cohort rhythm — a short daily practice, a bit of Scripture, and a question to carry into your day — straight to your inbox for a week. No cost, no commitment. When you're ready, the application will be waiting.
          </p>
          <TrialSignupForm compact />
        </div>
      </section>

      {/* BEFORE YOU CONTINUE */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-14">
        <div className="border border-[#D1C9BE] bg-[#EAE3D9]/30 p-10 md:p-12">
          <p className={overline + " mb-4"}>Before you continue</p>
          <h2 className="font-serif-display text-2xl md:text-3xl font-light mb-3">This experience probably isn't for everyone.</h2>
          <p className="text-[#5E4B3C] font-body mb-6">It's for people who are willing to:</p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 font-body text-[#2A2825]">
            {READINESS.map((r) => (<li key={r} className="flex items-start gap-3"><span className="text-[#A9724E] mt-1">✦</span>{r}</li>))}
          </ul>
          <p className="text-[#5E4B3C] font-body mt-8 italic">If that sounds like the season you're in, we'd love to hear your story.</p>
        </div>
      </section>

      {/* THE APPLICATION PROCESS */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-14">
        <p className={overline + " mb-4"}>The application process</p>
        <h2 className="font-serif-display text-3xl md:text-4xl font-light mb-4">How it unfolds, step by step.</h2>
        <p className="text-[#5E4B3C] font-body leading-relaxed max-w-2xl mb-12">Every application is personally reviewed because every Outpoured cohort is intentionally small and carefully formed.</p>
        <div className="space-y-2">
          {PROCESS.map((s) => (
            <div key={s.n} data-testid={`process-${s.n}`} className="flex gap-6 md:gap-10 py-7 border-b border-[#D8CEBE]">
              <span className="font-serif-display text-3xl md:text-4xl text-[#A9724E] shrink-0 w-12">{s.n}</span>
              <div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#2A2825] mb-2">{s.title}{s.note && <span className="font-body text-xs tracking-overline uppercase text-[#8a7a6a] ml-3">{s.note}</span>}</h3>
                <p className="text-[#5E4B3C] leading-relaxed font-body">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-[#2A2825] text-[#F4EFE6] grain relative">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="tracking-overline text-xs uppercase text-[#C79A6E] mb-4">The timeline</p>
          <h2 className="font-serif-display text-3xl md:text-4xl font-light mb-12">Winter 2027 at a glance.</h2>
          <div className="space-y-0">
            {TIMELINE.map((t, i) => (
              <div key={i} data-testid={`timeline-${i}`} className="grid md:grid-cols-3 gap-2 md:gap-8 py-6 border-b border-[#413d38]">
                <p className="font-serif-display text-lg md:text-xl text-[#E8B486] italic md:col-span-1">{t.when}</p>
                <p className="text-[#D9CFC2] font-body leading-relaxed md:col-span-2">{t.what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A FEW THINGS TO KNOW */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <p className={overline + " mb-6"}>A few things we want you to know</p>
        <ul className="space-y-5">
          {KNOW.map((k, i) => (
            <li key={i} className="flex items-start gap-3 text-[#2A2825] font-body leading-relaxed text-lg">
              <span className="text-[#A9724E] mt-1.5">✦</span>{k}
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-16 md:pb-20">
        <p className={overline + " mb-8"}>Questions</p>
        <div className="divide-y divide-[#D8CEBE] border-y border-[#D8CEBE]">
          {FAQ.map((item, i) => (
            <div key={i} data-testid={`faq-${i}`} className="py-7">
              <h3 className="font-serif-display text-xl md:text-2xl text-[#2A2825] mb-3">{item.q}</h3>
              <p className="text-[#5E4B3C] leading-relaxed font-body">{item.a}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-[#8a7a6a] font-body mt-8">Still have a question? Email <a href="mailto:dej@outpouredliving.com" className="text-[#A9724E] underline underline-offset-4">dej@outpouredliving.com</a>.</p>
      </section>

      {/* FORM */}
      <section id="application-form" className="max-w-3xl mx-auto px-6 md:px-12 pb-28">
        <form onSubmit={submit} data-testid="application-form" className="space-y-20">
          {/* S1 */}
          <div>
            <SectionTitle n="1" title="Basic Information" />
            <div className="grid sm:grid-cols-2 gap-6">
              <div><Label>First Name</Label><Input data-testid="app-firstName" value={f.firstName || ""} onChange={(e) => set("firstName", e.target.value)} className={inputCls} /></div>
              <div><Label>Last Name</Label><Input value={f.lastName || ""} onChange={(e) => set("lastName", e.target.value)} className={inputCls} /></div>
              <div><Label>Email</Label><Input data-testid="app-email" type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} /></div>
              <div><Label>Phone Number</Label><Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></div>
              <div><Label>Occupation</Label><Input value={f.occupation || ""} onChange={(e) => set("occupation", e.target.value)} className={inputCls} /></div>
              <div><Label>City</Label><Input value={f.city || ""} onChange={(e) => set("city", e.target.value)} className={inputCls} /></div>
              <div><Label>State</Label><Input value={f.state || ""} onChange={(e) => set("state", e.target.value)} className={inputCls} /></div>
              <div><Label>Relationship Status</Label><Input value={f.relationship_status || ""} onChange={(e) => set("relationship_status", e.target.value)} className={inputCls} /></div>
              <div><Label>Church <span className="text-[#8a7a6a]">(optional)</span></Label><Input value={f.church || ""} onChange={(e) => set("church", e.target.value)} className={inputCls} /></div>
              <div><Label>Instagram <span className="text-[#8a7a6a]">(optional)</span></Label><Input value={f.instagram || ""} onChange={(e) => set("instagram", e.target.value)} className={inputCls} /></div>
            </div>
          </div>

          {/* S2 */}
          <div>
            <SectionTitle n="2" title="Participation" />
            <div className="space-y-10">
              <div>
                <Label>Which cohort are you applying for?</Label>
                <RadioGroup value={f.cohort} onValueChange={(v) => set("cohort", v)} className="space-y-3 mt-3" data-testid="app-cohort">
                  {[["vessels", "VESSELS"], ["vows-and-vessels", "Vows & Vessels"]].map(([v, l]) => (
                    <label key={v} className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors duration-500 ${f.cohort === v ? "border-[#A9724E] bg-[#EAE3D9]/50" : "border-[#D1C9BE]"}`}><RadioGroupItem value={v} className="border-[#5E4B3C] text-[#A9724E]" /><span className="font-body">{l}</span></label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label>How would you like to participate?</Label>
                <RadioGroup value={f.participation} onValueChange={(v) => set("participation", v)} className="space-y-3 mt-3">
                  {PARTICIPATION.map(([v, l]) => (
                    <label key={v} className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors duration-500 ${f.participation === v ? "border-[#A9724E] bg-[#EAE3D9]/50" : "border-[#D1C9BE]"}`}><RadioGroupItem value={v} className="border-[#5E4B3C] text-[#A9724E]" /><span className="font-body">{l}</span></label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* S3 */}
          <div>
            <SectionTitle n="3" title="Your Story" />
            <div className="space-y-8">
              <div><Label hint="Not your résumé. Not your accomplishments. Just tell us about yourself and the experiences that have shaped who you are today. · Aim for 200–400 words.">Tell us your story.</Label><Textarea rows={5} value={f.story || ""} onChange={(e) => set("story", e.target.value)} className={areaCls} /></div>
              <div><Label hint="What's bringing you joy? What's been difficult? · Aim for 150–300 words.">What season of life are you currently in?</Label><Textarea rows={4} value={f.season || ""} onChange={(e) => set("season", e.target.value)} className={areaCls} /></div>
              <div><Label hint="What made you stop and think, “This is for me.” · Aim for 150–300 words.">Why are you applying for an Outpoured cohort?</Label><Textarea rows={4} value={f.why_applying || ""} onChange={(e) => set("why_applying", e.target.value)} className={areaCls} /></div>
              <div><Label>Where do you sense God inviting you to grow?</Label><Textarea rows={3} value={f.growth || ""} onChange={(e) => set("growth", e.target.value)} className={areaCls} /></div>
              <div><Label>What keeps you from becoming the person you believe God is calling you to be?</Label><Textarea rows={3} value={f.whats_keeping || ""} onChange={(e) => set("whats_keeping", e.target.value)} className={areaCls} /></div>
              <div><Label>When was the last time God surprised you?</Label><Textarea rows={3} value={f.god_surprised || ""} onChange={(e) => set("god_surprised", e.target.value)} className={areaCls} /></div>
              <div><Label hint="If yes: what helped? What still felt missing?">Have you participated in Bible studies, coaching, therapy, discipleship groups, or similar programs before?</Label><Textarea rows={4} value={f.prior_programs || ""} onChange={(e) => set("prior_programs", e.target.value)} className={areaCls} /></div>
              <div><Label hint="Who knows you well? Who encourages you? Who challenges you?">What does healthy community currently look like in your life?</Label><Textarea rows={4} value={f.community || ""} onChange={(e) => set("community", e.target.value)} className={areaCls} /></div>
            </div>
          </div>

          {/* S4 */}
          <div>
            <SectionTitle n="4" title="Looking Ahead" />
            <div className="space-y-8">
              <div><Label hint="How do you hope your relationship with God, your relationships, and your everyday life are different? Who do you hope you've become? · Aim for 150–300 words.">Imagine we're celebrating together six months from now. You look back and say, “This experience changed me.” What happened?</Label><Textarea rows={5} value={f.six_months || ""} onChange={(e) => set("six_months", e.target.value)} className={areaCls} /></div>
              <div className="border border-[#D1C9BE] p-6 bg-[#EAE3D9]/30">
                <p className="font-body text-[#2A2825] mb-3">This cohort includes:</p>
                <ul className="grid sm:grid-cols-2 gap-2 text-sm text-[#5E4B3C] font-body">
                  {["Weekly experiential gatherings", "Weekly virtual integration", "Daily partner connection", "Hands-on experiences", "Practical weekly challenges", "Intentional community"].map((x) => <li key={x} className="flex gap-2"><span className="text-[#A9724E]">·</span>{x}</li>)}
                </ul>
              </div>
              <div><Label>What excites you most? What feels stretching?</Label><Textarea rows={3} value={f.excites_stretching || ""} onChange={(e) => set("excites_stretching", e.target.value)} className={areaCls} /></div>
              <div>
                <Label>On a scale of 1–10, how willing are you to be challenged?</Label>
                <p className="text-xs text-[#8a7a6a] font-body -mt-1 mb-1">1 = hesitant · 10 = absolutely ready to be challenged</p>
                <div className="flex flex-wrap gap-2 mt-3" data-testid="app-willingness">
                  {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                    <button key={n} type="button" onClick={() => set("willingness", String(n))} className={`w-11 h-11 border font-body transition-colors duration-300 ${f.willingness === String(n) ? "border-[#A9724E] bg-[#A9724E] text-[#F4EFE6]" : "border-[#D1C9BE] text-[#5E4B3C] hover:border-[#A9724E]"}`}>{n}</button>
                  ))}
                </div>
              </div>
              <div><Label>Why did you choose that number?</Label><Textarea rows={2} value={f.willingness_why || ""} onChange={(e) => set("willingness_why", e.target.value)} className={areaCls} /></div>
              <div>
                <Label>Are you willing to prioritize this experience for the next six months?</Label>
                <RadioGroup value={f.prioritize} onValueChange={(v) => set("prioritize", v)} className="flex gap-3 mt-3">
                  {["Yes", "No", "Unsure"].map((v) => (<label key={v} className={`flex items-center gap-2 border px-5 py-3 cursor-pointer transition-colors duration-500 ${f.prioritize === v ? "border-[#A9724E] bg-[#EAE3D9]/50" : "border-[#D1C9BE]"}`}><RadioGroupItem value={v} className="border-[#5E4B3C] text-[#A9724E]" /><span className="font-body">{v}</span></label>))}
                </RadioGroup>
              </div>
              <div><Label>Is there anything happening in your life that may make this season difficult?</Label><Textarea rows={3} value={f.difficulties || ""} onChange={(e) => set("difficulties", e.target.value)} className={areaCls} /></div>
              <div className="border border-[#D1C9BE] bg-[#EAE3D9]/30 p-6">
                <Label hint="Don't let finances be a barrier. Scholarship and flexible-payment options are available — this stays between us and never affects how your application is reviewed.">Would you like to be considered for a scholarship or flexible-payment option?</Label>
                <RadioGroup value={f.scholarship_interest || ""} onValueChange={(v) => set("scholarship_interest", v)} className="flex flex-wrap gap-3 mt-3" data-testid="app-scholarship">
                  {["Yes", "Maybe", "No"].map((v) => (<label key={v} className={`flex items-center gap-2 border px-5 py-3 cursor-pointer transition-colors duration-500 ${f.scholarship_interest === v ? "border-[#A9724E] bg-[#EAE3D9]/60" : "border-[#D1C9BE]"}`}><RadioGroupItem value={v} className="border-[#5E4B3C] text-[#A9724E]" /><span className="font-body">{v}</span></label>))}
                </RadioGroup>
                {(f.scholarship_interest === "Yes" || f.scholarship_interest === "Maybe") && (
                  <div className="mt-6"><Label hint="Share a little about your why — your situation and what this experience would mean for you. We read every note personally.">Tell us about your why.</Label><Textarea data-testid="app-scholarship-why" rows={4} value={f.scholarship_why || ""} onChange={(e) => set("scholarship_why", e.target.value)} className={areaCls} /></div>
                )}
              </div>
            </div>
          </div>

          {/* S5 */}
          <div>
            <SectionTitle n="5" title="The Future" />
            <div className="space-y-8">
              <div>
                <Label hint="Check all that apply.">As Outpoured grows, we'd love to know how you might want to be involved beyond participating.</Label>
                <div className="space-y-2 mt-3">
                  {INVOLVEMENT.map((v) => (
                    <label key={v} className="flex items-start gap-3 border border-[#D1C9BE] p-3.5 cursor-pointer hover:border-[#5E4B3C]/40 transition-colors font-body text-[15px]">
                      <Checkbox checked={f.involvement.includes(v)} onCheckedChange={() => toggleInvolve(v)} className="mt-0.5 border-[#5E4B3C] data-[state=checked]:bg-[#A9724E] data-[state=checked]:border-[#A9724E]" /><span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>If you're outside Southern California, would you be interested in helping us launch Outpoured where you live?</Label>
                <RadioGroup value={f.launch_outside} onValueChange={(v) => set("launch_outside", v)} className="flex gap-3 mt-3">
                  {["Yes", "Maybe", "No"].map((v) => (<label key={v} className={`flex items-center gap-2 border px-5 py-3 cursor-pointer transition-colors duration-500 ${f.launch_outside === v ? "border-[#A9724E] bg-[#EAE3D9]/50" : "border-[#D1C9BE]"}`}><RadioGroupItem value={v} className="border-[#5E4B3C] text-[#A9724E]" /><span className="font-body">{v}</span></label>))}
                </RadioGroup>
              </div>
              <div><Label>Is there a church, ministry, organization, or community where you believe Outpoured could make an impact?</Label><Textarea rows={3} value={f.impact_place || ""} onChange={(e) => set("impact_place", e.target.value)} className={areaCls} /></div>
              <div><Label>What gifts or passions has God given you that you most enjoy using to serve others?</Label><Textarea rows={3} value={f.gifts || ""} onChange={(e) => set("gifts", e.target.value)} className={areaCls} /></div>
              <div><Label>Is there anything else you'd like us to know?</Label><Textarea rows={3} value={f.anything_else || ""} onChange={(e) => set("anything_else", e.target.value)} className={areaCls} /></div>
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="app-submit" className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-12 py-6 text-base tracking-wide transition-colors duration-500 disabled:opacity-60">
            {loading ? "Sending…" : "Begin My Journey"}<ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
          </Button>
        </form>
      </section>
    </div>
  );
}

const STEPS = [
  { n: "01", title: "Application Submitted", body: "Your story is in. Thank you for the courage it takes to share it.", done: true },
  { n: "02", title: "We Review", body: "By November 15, 2026, you'll receive an email with next steps — an invitation to a video conversation, or a heartfelt not-right-now." },
  { n: "03", title: "Discovery Conversation", body: "If invited, a relaxed 45–60 minute video call between November 15–30 to hear more of your story and answer your questions." },
  { n: "04", title: "Welcome to Winter 2027", body: "Acceptances go out November 30, with a welcome call in early December before your cohort launches." },
];

const ThankYou = ({ cohort, refNo }) => (
  <div className="bg-[#F4EFE6] text-[#2A2825] min-h-[80vh]">
    <section className="max-w-3xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10 text-center" data-testid="application-success">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }} className="w-16 h-16 rounded-full bg-[#A9724E]/15 flex items-center justify-center mx-auto mb-8">
        <Check className="w-7 h-7 text-[#A9724E]" strokeWidth={1.5} />
      </motion.div>
      <p className={overline + " mb-5"}>Thank you</p>
      <h1 className="font-serif-display font-light text-4xl sm:text-5xl leading-tight mb-6">Thank you for sharing your story.</h1>
      <p className="text-[#5E4B3C] leading-relaxed font-body text-lg max-w-2xl mx-auto mb-8">
        Every application is personally reviewed because every Outpoured cohort is intentionally small. <strong>By November 15, 2026, you'll hear from us with next steps.</strong> A confirmation with your reference number is on its way to your inbox.
      </p>
      {refNo && (
        <div data-testid="application-reference" className="inline-block border border-[#D8CFC0] bg-[#EAE3D9]/60 px-8 py-4">
          <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-1">Your reference number</p>
          <p className="font-serif-display text-2xl text-[#2A2825] tracking-wide">{refNo}</p>
        </div>
      )}
    </section>
    <section className="max-w-4xl mx-auto px-6 md:px-12 pb-28">
      <p className={overline + " mb-10 text-center"}>What happens next</p>
      <div className="grid md:grid-cols-4 gap-px bg-[#D1C9BE] border border-[#D1C9BE]">
        {STEPS.map((s) => (
          <div key={s.n} className={`p-8 ${s.done ? "bg-[#EAE3D9]/60" : "bg-[#F4EFE6]"}`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="font-serif-display text-3xl text-[#A9724E]">{s.n}</span>
              {s.done && <Check className="w-5 h-5 text-[#A9724E]" strokeWidth={2} />}
            </div>
            <h3 className="font-serif-display text-xl mb-2 leading-tight">{s.title}</h3>
            <p className="text-sm text-[#5E4B3C] leading-relaxed font-body">{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  </div>
);
